import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useExecutionImages } from '@/features/execution-images';
import type { ExecutionImageView } from '@/lib/api/generated/model';

/**
 * The "no override" value as a `Select` item — `Select` cannot carry a real
 * `null` value on an item, so this reserved string stands in for it and is
 * translated back to `null` in `onValueChange` below. Not a catalog label, so
 * it can never collide with a real image.
 */
const NO_OVERRIDE_VALUE = '$default';

const COPY = {
  workflow: {
    label: 'Execution image',
    noOverride: 'Use deployment default',
    loadFailed:
      'The execution image catalog could not be loaded. Reload to try again.',
    brokenReference:
      "The workflow's execution image no longer exists in the catalog. Choose a valid image, or clear the override, before this workflow can be saved.",
  },
  step: {
    label: 'Execution image',
    noOverride: 'Inherit from workflow',
    loadFailed:
      'The execution image catalog could not be loaded. Reload to try again.',
    brokenReference:
      "This step's execution image no longer exists in the catalog. Choose a valid image, or clear the override, before this step can be saved.",
  },
} as const;

/**
 * The one execution-image picker shared by the workflow-level and step-level
 * settings surfaces (spec 091, constitution §2.4 — one component, a `scope`
 * prop, not two near-duplicates).
 *
 * Mirrors `LlmProfilePicker`'s states in `StepInspector.tsx`, plus one this
 * feature adds: a `value` the current catalog result no longer contains
 * (contracts/ui-contract.md state 4). That state is never silently resolved
 * to `null` — the author must choose to clear it.
 */
export function ImagePicker({
  scope,
  workspaceId,
  value,
  onCommit,
}: {
  scope: 'workflow' | 'step';
  workspaceId: string;
  value: string | null;
  onCommit: (value: string | null) => void;
}) {
  const copy = COPY[scope];
  const { images, isPending, loadFailed } = useExecutionImages(workspaceId);

  if (isPending) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label>{copy.label}</Label>
        <Select value="" disabled>
          <SelectTrigger aria-label={copy.label}>
            <SelectValue>{() => 'Loading…'}</SelectValue>
          </SelectTrigger>
          <SelectContent />
        </Select>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label>{copy.label}</Label>
        <p role="alert" className="text-destructive text-xs">
          {copy.loadFailed}
        </p>
      </div>
    );
  }

  const catalog = images ?? [];
  const labelFor = (label: string): string | undefined =>
    catalog.find((entry: ExecutionImageView) => entry.label === label)?.label;
  const isBroken = value != null && labelFor(value) === undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{copy.label}</Label>
      <Select
        value={value ?? NO_OVERRIDE_VALUE}
        onValueChange={(next) => {
          onCommit(next === NO_OVERRIDE_VALUE ? null : next);
        }}
      >
        <SelectTrigger aria-label={copy.label}>
          <SelectValue>
            {(imageRef: string) => {
              if (imageRef === NO_OVERRIDE_VALUE) return copy.noOverride;
              // Never the raw reference — a label it no longer has is not
              // shown standing in for the label it used to have.
              return labelFor(imageRef) ?? 'Unknown image';
            }}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_OVERRIDE_VALUE}>{copy.noOverride}</SelectItem>
          {catalog.map((entry: ExecutionImageView) => (
            <SelectItem key={entry.id} value={entry.label}>
              {entry.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isBroken && (
        <p role="alert" className="text-destructive text-xs">
          {copy.brokenReference}
        </p>
      )}
    </div>
  );
}
